import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import { roleGroups } from '@/lib/roles';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useRoleCheck } from '@/hooks/useRoleCheck';

export interface OrgPolicySettingsAccess {
  canAccess: boolean;
  isLoading: boolean;
  isEnforcePolicyModeEnabled: boolean;
  isSecretConfidenceThresholdEnabled: boolean;
}

/**
 * Who may open Policies → Policy Settings (KIR-5106): an owner or manager with
 * either threshold flag on. Shared by the sidebar, UI v2 nav and route.
 */
export function useOrgPolicySettingsAccess(
  organizationId: string | undefined
): OrgPolicySettingsAccess {
  const { flags, isLoading: isFlagsLoading } = useFeatureFlags([
    flagsmithFeatureFlags.ENFORCE_POLICY_MODE,
    flagsmithFeatureFlags.SECRET_CONFIDENCE_THRESHOLD,
  ]);
  const { hasAccess: isManagerOrOwner, isLoading: isRoleLoading } =
    useRoleCheck(
      { param: 'organizationId', anyOf: [...roleGroups.managerOrOwner] },
      organizationId
    );

  const isEnforcePolicyModeEnabled =
    flags[flagsmithFeatureFlags.ENFORCE_POLICY_MODE] ?? false;
  const isSecretConfidenceThresholdEnabled =
    flags[flagsmithFeatureFlags.SECRET_CONFIDENCE_THRESHOLD] ?? false;

  return {
    canAccess:
      isManagerOrOwner &&
      (isEnforcePolicyModeEnabled || isSecretConfidenceThresholdEnabled),
    isLoading: isFlagsLoading || isRoleLoading,
    isEnforcePolicyModeEnabled,
    isSecretConfidenceThresholdEnabled,
  };
}
